import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Lenis from '@studio-freight/lenis';
import { PencilSimple, Trash, Eye, Lightbulb } from "@phosphor-icons/react";
import ProductSubmissionForm from "@/components/ProductSubmissionForm";
import ProductSubmissionFormFull from "@/components/ProductSubmissionFormFull";
import ProductEditForm from "@/components/ProductEditForm";
import PwaInstall from "@/components/PwaInstall";
import NotificationPrompt from "@/components/NotificationPrompt";
import WelcomePopup from "@/components/WelcomePopup";
import CampaignPopup from "@/components/CampaignPopup";
import InlineBanner from "@/components/InlineBanner";
import { isGated } from "@/lib/campaignFrequency";
import PaginationLoader from "@/components/PaginationLoader";
import YourStorySection, { StoryStrip } from "@/components/YourStorySection";
import OfflineSpotsSection from "@/components/OfflineSpotsSection";
import OfflineSpotComposer from "@/components/OfflineSpotComposer";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import WelcomeRedirect from "@/pages/WelcomeRedirect";
import PendingReview from "@/pages/PendingReview";
import AdminPanel from "@/pages/AdminPanel";
import Stories from "@/pages/Stories";
import Bookmarks from "@/pages/Bookmarks";
import Profile from "@/pages/Profile";
import ProductDetail from "@/pages/ProductDetail";
import InseamGuide from "@/pages/InseamGuide";
import OfflineSpots from "@/pages/OfflineSpots";
import OfflineSpotDetail from "@/pages/OfflineSpotDetail";
import Settings from "@/pages/Settings";
import { About, Contact, Privacy, Terms } from "@/pages/Legal";
import { sortHeights } from "@/lib/heights";
import {
  track,
  trackPageView,
  setAnalyticsUser,
  itemFromProduct,
  listName,
} from "@/lib/track";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Minimum time the giraffe pagination loader is shown before the next batch of
// products is revealed below it. Keeps the sequence: current products → giraffe
// (for this long) → next products load in — even when the fetch returns fast.
const PAGINATION_LOADER_MS = 3000;

// A product wears the "Newly added" badge for its first NEWLY_ADDED_DAYS days.
// Computed live from created_at (ISO, UTC) on every render, so the badge appears
// the moment a product is shared and disappears on its own — no flag, no cron.
const NEWLY_ADDED_DAYS = 7;
const isNewlyAdded = (createdAt) => {
  if (!createdAt) return false;
  const ts = Date.parse(createdAt);
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts < NEWLY_ADDED_DAYS * 24 * 60 * 60 * 1000;
};

// Snapshot of the feed (loaded products + filters + scroll position) captured
// when opening a product, so pressing Back returns to the exact same place with
// the same products already loaded — instead of a reset page-1 feed at the top.
let feedSnapshot = null;

const Discovery = () => {
  const PAGE_SIZE = 12;
  const snap = feedSnapshot; // read once at mount; null on a normal load
  const [products, setProducts] = useState(snap ? snap.products : []);
  const [total, setTotal] = useState(snap ? snap.total : 0);
  const [hasMore, setHasMore] = useState(snap ? snap.hasMore : false);
  const [showForm, setShowForm] = useState(false);
  const [showSpotForm, setShowSpotForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(snap ? false : true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [selectedHeight, setSelectedHeight] = useState(snap ? snap.selectedHeight : 'all');
  const [selectedCategory, setSelectedCategory] = useState(snap ? snap.selectedCategory : 'all');
  const [selectedPrice, setSelectedPrice] = useState(snap ? snap.selectedPrice : 'all'); // price bucket | 'all'
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedTab, setSelectedTab] = useState(snap ? snap.selectedTab : 'all'); // category_id | 'all'
  const [filtersHidden, setFiltersHidden] = useState(false); // hide filter bar on scroll-down
  const [campaigns, setCampaigns] = useState([]); // marketing banners matching the current tab/user
  const lenisRef = useRef(null); // Lenis smooth-scroll instance (for scroll-to-top + direction)
  // When restoring a snapshot, prefill is already reflected in the restored
  // filters — mark it done so it doesn't override them, and skip the initial
  // fetch/scroll-reset so the cached products + scroll position survive.
  const heightPrefilledRef = useRef(!!snap);
  const genderTabAppliedRef = useRef(!!snap);
  const restoreMountRef = useRef(!!snap);
  // Keep latest state available to the observer callback without re-creating it.
  const fetchStateRef = useRef({ loading: true, loadingMore: false, hasMore: false, loaded: 0 });
  useEffect(() => {
    fetchStateRef.current = { loading, loadingMore, hasMore, loaded: products.length };
  });
  // Keep a ref to the latest fetchProducts so the observer can always call it
  // with the current filter/state closure.
  const fetchProductsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current user can edit (only swarajrp030@gmail.com)
  const canEdit = user?.email === 'swarajrp030@gmail.com';

  // Compute heights available in the height filter based on the selected tab.
  // - Specific tab → that category's heights
  // - "All" → union of all category heights
  // - No categories at all → fallback to the legacy 6.0–6.5 set
  const DEFAULT_HEIGHTS = ['6.0', '6.1', '6.2', '6.3', '6.4', '6.5'];
  let availableHeights;
  if (selectedTab !== 'all') {
    const cat = categories.find((c) => c.id === selectedTab);
    availableHeights = sortHeights(cat?.heights?.length ? cat.heights : DEFAULT_HEIGHTS);
  } else if (categories.length > 0) {
    const set = new Set();
    categories.forEach((c) => (c.heights || []).forEach((h) => set.add(h)));
    availableHeights = sortHeights(set.size > 0 ? Array.from(set) : DEFAULT_HEIGHTS);
  } else {
    availableHeights = DEFAULT_HEIGHTS;
  }

  // The category behind the active tab drives labels/options (clothing vs shoes).
  const selectedCat =
    selectedTab !== 'all' ? categories.find((c) => c.id === selectedTab) : null;
  const isShoes = selectedCat?.kind === 'shoes';
  const sizeLabel = (selectedCat?.size_label || 'ft').trim() || 'ft';
  const typeOptions =
    selectedCat?.types?.length ? selectedCat.types : ['Upper', 'Lower'];

  // Readable name for the active tab, used as the GA4 item_list_name. The raw
  // category_id is a UUID — accurate but unreadable in a report, and mixing the
  // two forms would split one list across two rows. Returns null while the name
  // can't be resolved yet (see the impression queue below).
  const tabLabel = () => {
    if (selectedTab === 'all') return 'all';
    const cat = categories.find((c) => c.id === selectedTab);
    return cat ? cat.name : null;
  };

  // Restoring the feed snapshot (coming Back from a product page) puts a
  // category_id into selectedTab before the categories fetch has resolved, so an
  // impression can be ready before its list has a readable name. Queue those and
  // flush once the names land — or once the categories request has settled, so a
  // failed fetch loses the name rather than the whole impression.
  const pendingImpressions = useRef([]);
  // State, not a ref: flipping it must trigger the render whose effect drains
  // the queue with a fresh (categories-populated) closure.
  const [categoriesSettled, setCategoriesSettled] = useState(false);
  const emitImpression = (payload, label) => {
    if (label === null && !categoriesSettled) {
      pendingImpressions.current.push(payload);
      return;
    }
    const list = listName({ ...payload.filters, tab: label ?? payload.filters.tab });
    track('view_item_list', {
      item_list_id: list,
      item_list_name: list,
      page_number: payload.pageNumber,
      items: payload.items.map((p, i) =>
        itemFromProduct(p, { index: payload.startIndex + i, item_list_name: list })
      ),
    });
  };
  const flushImpressions = () => {
    if (pendingImpressions.current.length === 0) return;
    if (categories.length === 0 && !categoriesSettled) return;
    const queued = pendingImpressions.current;
    pendingImpressions.current = [];
    queued.forEach((payload) => emitImpression(payload, tabLabel()));
  };
  useEffect(flushImpressions);

  // Server-side fetch with current filters + pagination.
  // - reset=true → replaces the list (fresh page 1)
  // - reset=false → appends the next page
  const fetchProducts = async ({ reset = true } = {}) => {
    if (reset) setLoading(true); else setLoadingMore(true);
    const startedAt = Date.now();
    try {
      const params = { limit: PAGE_SIZE };
      params.offset = reset ? 0 : products.length;
      if (selectedTab !== 'all') params.category_id = selectedTab;
      if (selectedHeight !== 'all') params.height = selectedHeight;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedPrice !== 'all') params.price_bucket = selectedPrice;

      const response = await axios.get(`${API}/products`, { params });
      const { items = [], total: t = 0, has_more: more = false } = response.data || {};

      // On pagination, hold the giraffe loader for its minimum beat before
      // revealing the next batch below the existing products.
      if (!reset) {
        const wait = PAGINATION_LOADER_MS - (Date.now() - startedAt);
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
      }

      setProducts((prev) => (reset ? items : [...prev, ...items]));
      setTotal(t);
      setHasMore(more);

      // GA4 view_item_list — the impressions half of the funnel. Without these
      // we have clicks but no CTR, so a product nobody scrolled past looks the
      // same as one everybody ignored. Fired per page, with the running index
      // preserved across pagination so position analysis stays meaningful.
      const startIndex = reset ? 0 : products.length;
      const filters = {
        tab: selectedTab,
        height: selectedHeight,
        category: selectedCategory,
        price: selectedPrice,
      };
      if (items.length > 0) {
        emitImpression(
          {
            items,
            startIndex,
            filters,
            pageNumber: Math.floor(startIndex / PAGE_SIZE) + 1,
          },
          tabLabel()
        );
      }
      // Only on pagination: reaching the end of a short first page isn't a
      // depth signal, but paginating until there's nothing left is.
      if (!reset && !more) {
        track('feed_end_reached', {
          item_list_name: listName({ ...filters, tab: tabLabel() ?? selectedTab }),
          items_loaded: startIndex + items.length,
          total_available: t,
        });
      }
    } catch (e) {
      console.error("Error fetching products:", e);
    } finally {
      if (reset) setLoading(false); else setLoadingMore(false);
    }
  };
  // Keep the latest fetchProducts closure reachable from the observer callback.
  useEffect(() => {
    fetchProductsRef.current = fetchProducts;
  });

  // Remember the active height/size filter (and which kind of tab it came from)
  // so the product detail page can recommend "great fits for <height>" to
  // signed-out browsers who've expressed intent but have no saved profile.
  useEffect(() => {
    try {
      if (selectedHeight && selectedHeight !== 'all') {
        sessionStorage.setItem('nos_filter_height', selectedHeight);
        sessionStorage.setItem('nos_filter_is_shoe', isShoes ? '1' : '0');
      } else {
        sessionStorage.removeItem('nos_filter_height');
        sessionStorage.removeItem('nos_filter_is_shoe');
      }
    } catch (_) {
      /* sessionStorage unavailable — the filter signal just won't carry over */
    }
  }, [selectedHeight, isShoes]);

  // Ref-callback for the infinite-scroll sentinel. Rebinds the observer to
  // whatever DOM node is currently mounted — this fixes the bug where the
  // observer would keep watching a stale (unmounted) sentinel after a
  // filter/tab change whose new page happened to have the same product
  // count + hasMore as before (deps unchanged, effect wouldn't re-run).
  const setLoadMoreSentinel = useCallback((node) => {
    if (setLoadMoreSentinel._observer) {
      setLoadMoreSentinel._observer.disconnect();
      setLoadMoreSentinel._observer = null;
    }
    if (!node) return;
    setLoadMoreSentinel._observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        const s = fetchStateRef.current;
        if (s.loading || s.loadingMore || !s.hasMore) return;
        // Scroll depth, in pages. The sentinel firing means the reader got to
        // the bottom of what was loaded — the closest thing this feed has to a
        // scroll-depth metric.
        track('feed_load_more', { page_number: Math.floor(s.loaded / PAGE_SIZE) + 1 });
        fetchProductsRef.current?.({ reset: false });
      },
      { rootMargin: '400px 0px' }
    );
    setLoadMoreSentinel._observer.observe(node);
  }, []);

  // Re-fetch from page 1 whenever filters or tab change. Skipped on a restore
  // mount so the snapshot's already-loaded products aren't wiped by a reset.
  useEffect(() => {
    if (restoreMountRef.current) return;
    fetchProducts({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, selectedHeight, selectedCategory, selectedPrice]);

  // When the section OR any filter changes, jump back to the top and show the
  // filter bar. Snapping to a stable at-top state also prevents a flicker loop
  // (content reflow -> Lenis scroll events -> filter-bar collapse -> reflow…).
  // Skipped on a restore mount — we want to keep the saved scroll position.
  useEffect(() => {
    if (restoreMountRef.current) return;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
    setFiltersHidden(false);
  }, [selectedTab, selectedHeight, selectedCategory, selectedPrice]);

  // Restore mount: put the scroll back where it was, then clear the one-shot
  // snapshot + flag so later filter changes behave normally.
  useLayoutEffect(() => {
    if (!snap) return;
    const y = snap.scrollY || 0;
    // Lenis inits in its own effect (after this one); defer a frame so it's ready.
    requestAnimationFrame(() => {
      if (lenisRef.current) lenisRef.current.scrollTo(y, { immediate: true });
      else window.scrollTo(0, y);
    });
    feedSnapshot = null;
    // Let this mount's fetch/scroll effects see the flag, then release it.
    const t = setTimeout(() => { restoreMountRef.current = false; }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Check authentication
    const checkAuth = async () => {
      // If user data passed from AuthCallback or Onboarding, use it
      if (location.state?.user) {
        setUser(location.state.user);
        setAuthChecking(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (e) {
        // Not logged in, that's fine - can still view
        setUser(null);
      } finally {
        setAuthChecking(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data || []);
      } catch (e) {
        console.error('Error fetching categories:', e);
      } finally {
        // Marks the point after which a queued impression should be released
        // even if we still can't name its list — a lost label beats a lost
        // event. The drain itself happens in the effect below, after the
        // re-render, so the label is resolved against the loaded categories.
        setCategoriesSettled(true);
      }
    };

    const fetchBrands = async () => {
      try {
        const res = await axios.get(`${API}/brands`);
        setBrands(res.data || []);
      } catch (e) {
        console.error('Error fetching brands:', e);
      }
    };

    checkAuth();
    fetchCategories();
    fetchBrands();
  }, [location.state]);

  // Attach identity to every subsequent GA4 hit. Runs once auth resolves so we
  // don't stamp an anonymous session as logged-out and then correct it. Height
  // and gender become user properties — the two segments worth slicing this
  // product by. No email or name is ever sent.
  useEffect(() => {
    if (authChecking) return;
    setAnalyticsUser(user);
  }, [user, authChecking]);

  // The nav's Create button and the product-detail "Share" CTA route here with an
  // intent flag. Wait for auth to resolve, then open the Share composer if signed
  // in (or send to login if not), and clear the flag so back/refresh won't reopen.
  useEffect(() => {
    if (!location.state?.openCreate || authChecking) return;
    navigate(location.pathname, { replace: true, state: {} }); // consume the flag
    if (user) {
      setShowForm(true);
    } else {
      toast.error('Please sign in to share a fit');
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, authChecking, user]);

  // Load marketing banners targeted at the current viewer + category tab.
  // Re-runs on tab change and when auth resolves (gender/registered gating).
  useEffect(() => {
    if (authChecking) return;
    axios
      .get(`${API}/campaigns/feed`, {
        params: { category_id: selectedTab },
        withCredentials: true,
      })
      .then((res) => setCampaigns(res.data || []))
      .catch(() => setCampaigns([]));
  }, [selectedTab, user, authChecking]);

  // If switching tabs makes the current height invalid for that category,
  // reset to "all" so the user isn't stuck with an empty grid.
  useEffect(() => {
    if (selectedHeight !== 'all' && !availableHeights.includes(selectedHeight)) {
      setSelectedHeight('all');
    }
    // Type options differ per tab (Upper/Lower vs Sports/Casual/…); drop a
    // stale selection so the grid isn't stuck empty after switching tabs.
    if (selectedCategory !== 'all' && !typeOptions.includes(selectedCategory)) {
      setSelectedCategory('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, categories]);

  // Pre-fill height filter with the user's profile height once per session.
  // Only applies to logged-in users; non-logged-in users see all heights.
  // After this, manual changes to the filter are preserved.
  // Valid heights come from the user's gender category (women's list differs
  // from men's), falling back to the union of all categories.
  useEffect(() => {
    if (!user || heightPrefilledRef.current) return;
    if (categories.length === 0) return; // wait until categories load
    const wantName = user.gender === 'female' ? 'women' : user.gender === 'male' ? 'men' : null;
    const cat = wantName
      ? categories.find((c) => (c.name || '').toLowerCase() === wantName)
      : null;
    const validHeights = cat?.heights?.length
      ? cat.heights
      : categories.flatMap((c) => c.heights || []);
    if (user.height && validHeights.includes(user.height)) {
      setSelectedHeight(user.height);
    }
    heightPrefilledRef.current = true;
  }, [user, categories]);

  // On first load, drop a logged-in user into their section (Men/Women) based
  // on their gender. Runs once; manual tab changes afterward are preserved.
  useEffect(() => {
    if (genderTabAppliedRef.current) return;
    if (!user || !user.gender || categories.length === 0) return;
    const wantName = user.gender === 'female' ? 'women' : 'men';
    const cat = categories.find((c) => (c.name || '').toLowerCase() === wantName);
    if (cat) {
      setSelectedTab(cat.id);
      genderTabAppliedRef.current = true;
    }
  }, [user, categories]);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    // Hide the Height/Category filter bar when scrolling down; reveal on any
    // upward scroll. Always show near the top. A hysteresis threshold ignores
    // small scroll jitters so the bar/button don't flicker with Lenis momentum.
    let lastY = 0;
    const THRESHOLD = 24;
    lenis.on('scroll', (e) => {
      const y = e.scroll ?? e.animatedScroll ?? 0;
      if (y < 90) {
        setFiltersHidden(false);
        lastY = y;
        return;
      }
      const delta = y - lastY;
      if (Math.abs(delta) < THRESHOLD) return; // ignore jitter; keep current state
      setFiltersHidden(delta > 0); // down -> hide, up -> show
      lastY = y;
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchProducts({ reset: true });
  };

  const handleEditFormSuccess = () => {
    setShowEditForm(false);
    setEditingProduct(null);
    fetchProducts({ reset: true });
  };

  const handleEditClick = (product, e) => {
    e.stopPropagation(); // Prevent image click handler
    setEditingProduct(product);
    setShowEditForm(true);
  };

  const handleDeleteClick = async (product, e) => {
    e.stopPropagation(); // Prevent image click handler
    if (!window.confirm(`Delete "${product.product_name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/products/${product.id}`, { withCredentials: true });
      toast.success('Product deleted');
      fetchProducts({ reset: true });
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Could not delete product');
    }
  };

  // `source` names which CTA was tapped, so we can tell the header button from
  // the empty-state one from the nav rail. The contribution loop is the growth
  // engine here and none of it was measured before.
  const handleShareClick = (source = 'unknown') => {
    track('share_fit_click', {
      source,
      is_logged_in: !!user,
      // A signed-out tap is a lost contributor, not a click — worth separating.
      outcome: user ? 'opened_form' : 'sent_to_login',
    });
    if (!user) {
      toast.error('Please sign in to share a fit');
      navigate('/login');
      return;
    }
    setShowForm(true);
  };

  // Offline spots need a signed-in sharer for the same reason fits do: the tab
  // a spot lands in is derived from the sharer's gender, and an anonymous
  // recommendation is worth little.
  const handleSpotShareClick = () => {
    if (!user) {
      toast.error('Please sign in to share a spot');
      navigate('/login');
      return;
    }
    setShowSpotForm(true);
  };

  // Current filter set, as the item_list_name that view_item_list used — so a
  // select_item can be attributed back to the exact list it was clicked from.
  const currentList = () =>
    listName({
      tab: tabLabel() ?? selectedTab,
      height: selectedHeight,
      category: selectedCategory,
      price: selectedPrice,
    });

  const handleImageClick = (product) => {
    // Cards open the product's detail page (image or title). The visit is counted
    // there, alongside the retailer link + community fit. Browsing needs no sign-in.
    const list = currentList();
    track('select_item', {
      item_list_id: list,
      item_list_name: list,
      items: [
        itemFromProduct(product, {
          index: products.indexOf(product),
          item_list_name: list,
        }),
      ],
      // Legacy params, so the dual-fired product_detail_open keeps its old shape.
      product_name: product.product_name,
      product_category: product.category,
      product_link: product.product_link,
      image_link: product.image_url,
      event_category: 'Product Engagement',
      event_label: product.product_name,
    });
    // Snapshot the feed so Back returns to this exact spot with these products.
    feedSnapshot = {
      selectedTab, selectedHeight, selectedCategory, selectedPrice,
      products, total, hasMore,
      scrollY: (typeof window !== 'undefined' && window.scrollY) || 0,
    };
    navigate(`/product/${product.id}`);
  };

  // Logo / "home": reset to the full feed with filters cleared.
  const goHome = () => {
    setSelectedTab('all');
    setSelectedHeight('all');
    setSelectedCategory('all');
    setSelectedPrice('all');
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    navigate('/');
  };

  // The category tab that matches the logged-in user's gender (their "own" tab).
  const userGenderTabId = (() => {
    if (!user || !user.gender || categories.length === 0) return null;
    const name = user.gender === 'female' ? 'women' : 'men';
    const cat = categories.find((c) => (c.name || '').toLowerCase() === name);
    return cat ? cat.id : null;
  })();

  // Map brand_id -> brand (for the logo tag on product cards).
  const brandById = brands.reduce((acc, b) => {
    acc[b.id] = b;
    return acc;
  }, {});

  const categoryById = categories.reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  // Switching tabs clears the filters, EXCEPT returning to the user's own
  // gender tab re-applies their profile height. The "All" tab always clears.
  const handleTabClick = (tabId) => {
    track('tab_select', {
      tab_id: tabId,
      tab_name: tabId === 'all' ? 'All' : categoryById[tabId]?.name || tabId,
      previous_tab_id: selectedTab,
      is_own_gender_tab: tabId === userGenderTabId,
    });
    setSelectedTab(tabId);
    setSelectedCategory('all');
    setSelectedPrice('all');
    if (tabId !== 'all' && tabId === userGenderTabId && user?.height) {
      setSelectedHeight(user.height);
    } else {
      setSelectedHeight('all');
    }
  };

  // One handler for all three filter selects. Which heights and price bands
  // people actually filter for is the most product-relevant signal this feed
  // produces, and it was previously invisible.
  const handleFilterChange = (filterName, value, setter) => {
    track('filter_apply', {
      filter_name: filterName,
      filter_value: value,
      tab_id: selectedTab,
      // A cleared select is a real signal too — distinguish it from a set one.
      is_cleared: value === 'all',
    });
    setter(value);
  };

  // Inline marketing banners are injected into the grid as full-width rows.
  // Each sits after its configured product count (clamped to the list length).
  // Honour the same "How often" cap as popups: a gated banner is skipped for
  // this load and marks itself seen once scrolled into view (see InlineBanner).
  const inlineCampaigns = campaigns.filter(
    (c) => c.placement === 'inline' && !isGated(c)
  );
  const renderInlineBanner = (c) => (
    <InlineBanner key={`camp-${c.id}`} campaign={c} onShareClick={() => handleShareClick('inline_banner')} />
  );
  const bannersAt = (pos) =>
    inlineCampaigns.filter((c) => Math.min(c.inline_after ?? 0, products.length) === pos);

  return (
    <div className="min-h-screen">
      {/* Welcome Popup - Shows once on first visit */}
      <WelcomePopup />

      {/* Top progress indicator — visible during any product fetch */}
      {(loading || loadingMore) && (
        <div
          className="fixed top-0 left-0 right-0 h-0.5 z-[60] overflow-hidden bg-transparent"
          data-testid="top-progress-bar"
          aria-hidden="true"
        >
          <div className="h-full w-1/3 bg-[#2D302A] top-progress-slide" />
        </div>
      )}

      {/* Header */}
      <header 
        className="sticky top-0 z-50 bg-[#F9F8F6]/90 backdrop-blur-xl border-b border-[#E4E1D9] py-3 md:py-4 px-4 md:px-12 flex justify-between items-center"
        data-testid="header"
      >
        <h1 
          className="text-2xl md:text-3xl tracking-tighter cursor-pointer" 
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
          onClick={goHome}
          data-testid="site-title"
        >
          North of Six Club
        </h1>
        
        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={() => {
              track('inseam_guide_open', { source: 'feed_header' });
              navigate('/inseam-guide');
            }}
            className="p-2 hover:bg-[#F0EFEB] rounded-md transition-colors"
            title="Inseam guide — how to measure & find your fit"
            aria-label="Inseam guide"
            data-testid="inseam-guide-button"
          >
            <Lightbulb size={22} weight="fill" className="text-[#2D302A]" />
          </button>
          <PwaInstall user={user} />
          {authChecking ? (
            <div className="h-8 w-8 animate-pulse bg-[#E4E1D9] rounded-full"></div>
          ) : user && canEdit ? (
            <button
              onClick={() => navigate('/admin')}
              className="text-xs uppercase tracking-[0.2em] text-[#2D302A] hover:underline"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="admin-link"
            >
              Admin
            </button>
          ) : null}
        </div>
      </header>

      {/* Sticky nav: category tabs + Height/Category filters stay pinned below the header on scroll */}
      <div className="sticky top-[57px] md:top-[65px] z-40">
      {/* Category Tabs (admin-managed). "All" is always first. */}
      <div
        className="bg-[#F9F8F6] border-b border-[#E4E1D9] px-4 md:px-12"
        data-testid="category-tabs"
      >
        <div className="flex overflow-x-auto no-scrollbar gap-2 md:gap-6 py-2">
          <TabPill
            active={selectedTab === 'all'}
            onClick={() => handleTabClick('all')}
            testid="category-tab-all"
          >
            All
          </TabPill>
          {categories.map((c) => (
            <TabPill
              key={c.id}
              active={selectedTab === c.id}
              onClick={() => handleTabClick(c.id)}
              testid={`category-tab-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {c.name}
            </TabPill>
          ))}
        </div>
      </div>

      {/* Filters Section — collapses on scroll-down, reappears on scroll-up.
          Hidden entirely on the All tab: height mixes men/women, type isn't
          meaningful across the whole feed, and price isn't offered there. On
          mobile/tablet the row stays on a single line (scrolls if it overflows)
          rather than wrapping. */}
      {selectedTab !== 'all' && (
        <div
          className={`bg-[#F9F8F6] border-b border-[#E4E1D9] px-4 md:px-12 overflow-hidden transition-all duration-300 ${filtersHidden ? 'max-h-0 py-0 opacity-0 border-b-0' : 'max-h-24 py-3 opacity-100'}`}
        >
          <div className="flex flex-row flex-nowrap gap-2 md:gap-4 items-center overflow-x-auto no-scrollbar">
            <span
              className="hidden md:inline shrink-0 text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              Filters:
            </span>

            {/* Height Filter (shoe size on shoe tabs) — always first */}
            <select
              value={selectedHeight}
              onChange={(e) => handleFilterChange(isShoes ? 'shoe_size' : 'height', e.target.value, setSelectedHeight)}
              className="shrink-0 px-2 md:px-4 py-1.5 md:py-2 bg-white border border-[#E4E1D9] rounded-md text-xs md:text-sm focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="height-filter"
            >
              <option value="all">{isShoes ? 'All Sizes' : 'All Heights'}</option>
              {availableHeights.map((h) => (
                <option key={h} value={h}>{isShoes ? `${sizeLabel} ${h}` : `${h} ${sizeLabel}`}</option>
              ))}
            </select>

            {/* Price Filter — second */}
            <select
              value={selectedPrice}
              onChange={(e) => handleFilterChange('price', e.target.value, setSelectedPrice)}
              className="shrink-0 px-2 md:px-4 py-1.5 md:py-2 bg-white border border-[#E4E1D9] rounded-md text-xs md:text-sm focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="price-filter"
            >
              <option value="all">All Prices</option>
              <option value="under_1499">Under ₹1,499</option>
              <option value="1500_3999">₹1,500 - ₹3,999</option>
              <option value="over_4000">Over ₹4,000</option>
            </select>

            {/* Category (type) Filter — options come from the tab's category */}
            <select
              value={selectedCategory}
              onChange={(e) => handleFilterChange('type', e.target.value, setSelectedCategory)}
              className="shrink-0 px-2 md:px-4 py-1.5 md:py-2 bg-white border border-[#E4E1D9] rounded-md text-xs md:text-sm focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="category-filter"
            >
              <option value="all">All Categories</option>
              {typeOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            {/* Clear Filters */}
            {(selectedHeight !== 'all' || selectedCategory !== 'all' || selectedPrice !== 'all') && (
              <button
                onClick={() => {
                  track('filter_clear', {
                    tab_id: selectedTab,
                    cleared_height: selectedHeight,
                    cleared_type: selectedCategory,
                    cleared_price: selectedPrice,
                  });
                  setSelectedHeight('all');
                  setSelectedCategory('all');
                  setSelectedPrice('all');
                }}
                className="shrink-0 text-[10px] md:text-xs text-[#2D302A] hover:underline"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                data-testid="clear-filters"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Main Content */}
      <main className="px-6 md:px-12 py-12 md:py-24">
        {/* Your Story — full section on the unfiltered All tab, compact strip
            on the Men/Women tabs */}
        {!loading &&
          selectedTab === 'all' &&
          selectedHeight === 'all' &&
          selectedCategory === 'all' &&
          selectedPrice === 'all' && <YourStorySection user={user} />}
        {!loading && selectedTab !== 'all' && <StoryStrip />}

        {/* Offline spots — a compact strip, the same shape as the Story strip.
            The full grid lives on its own nav destination (/offline-spots).
            Scoped to the active tab: "All" shows every spot; a category tab
            shows only the ones filed under it (the tab is decided server-side
            from the sharer's gender + what they bought). */}
        {!loading && (
          <OfflineSpotsSection
            categoryId={selectedTab}
            categories={categories}
            user={user}
            onShare={handleSpotShareClick}
          />
        )}

        {loading ? (
          <div
            className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 md:gap-x-8 gap-y-8 md:gap-y-16"
            data-testid="loading-state"
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <ProductCardSkeleton key={i} index={i} />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <h2 
              className="text-3xl md:text-4xl mb-4 tracking-tighter" 
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {(selectedTab !== 'all' || selectedHeight !== 'all' || selectedCategory !== 'all' || selectedPrice !== 'all') ? 'No Matching Fits' : 'No Fits Yet'}
            </h2>
            <p className="text-[#6B6A68] mb-8">
              {(selectedTab !== 'all' || selectedHeight !== 'all' || selectedCategory !== 'all' || selectedPrice !== 'all')
                ? 'Try adjusting your filters to see more products.'
                : 'Be the first to share a product that fits perfectly.'}
            </p>
            {selectedTab === 'all' && selectedHeight === 'all' && selectedCategory === 'all' && selectedPrice === 'all' && (
              <button
                onClick={() => handleShareClick('empty_state')}
                className="bg-[#2D302A] text-white px-6 py-3 rounded-md hover:bg-[#1A1C18] transition-colors duration-300"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                data-testid="empty-share-button"
              >
                <span className="text-xs uppercase tracking-[0.2em]">Share Your First Fit</span>
              </button>
            )}
          </div>
        ) : (
          <>
          <div 
            className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 md:gap-x-8 gap-y-8 md:gap-y-16"
            data-testid="products-grid"
          >
            {/* Inline banner configured to sit before any product */}
            {bannersAt(0).map(renderInlineBanner)}
            {products.map((product, index) => (
              <Fragment key={product.id}>
              <div
                className="group"
                // Stagger the fade-in per page (index within the current batch)
                // so each newly paginated batch starts appearing immediately —
                // in milliseconds — as the giraffe sunsets, instead of waiting
                // on the ever-growing absolute index.
                style={{ animation: `fadeUp 0.5s ease-out ${(index % PAGE_SIZE) * 0.04}s both` }}
                data-testid={`product-card-${index}`}
              >
                {/* Image - Clickable */}
                <div 
                  className="relative overflow-hidden bg-white border border-[#E4E1D9] mb-4 cursor-pointer"
                  onClick={() => handleImageClick(product)}
                  data-testid={`product-image-container-${index}`}
                >
                  <img
                    src={`${BACKEND_URL}${product.image_url}`}
                    alt={product.product_name}
                    className="w-full aspect-[1/2] object-cover hover:scale-[1.02] transition-transform duration-500 ease-out"
                    data-testid={`product-image-${index}`}
                  />

                  {/* Brand tag (logo + name) — top-left, only if the product has a brand */}
                  {brandById[product.brand_id] && (
                    <div
                      className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm rounded-full p-0.5 md:p-1 md:pr-2.5 shadow-md"
                      data-testid={`product-brand-${index}`}
                    >
                      {brandById[product.brand_id].logo_url && (
                        <img
                          src={`${BACKEND_URL}${brandById[product.brand_id].logo_url}`}
                          alt={brandById[product.brand_id].name}
                          className="w-8 h-8 md:w-5 md:h-5 rounded-full object-cover"
                        />
                      )}
                      <span
                        className="hidden md:inline text-[10px] uppercase tracking-[0.1em] text-[#2D302A]"
                        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                      >
                        {brandById[product.brand_id].name}
                      </span>
                    </div>
                  )}

                  {/* Edit + Delete Buttons - Only for authorized user */}
                  {canEdit && (
                    <button
                      onClick={(e) => handleEditClick(product, e)}
                      className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110"
                      data-testid={`edit-product-${index}`}
                      title="Edit product"
                    >
                      <PencilSimple size={18} weight="bold" className="text-[#2D302A]" />
                    </button>
                  )}
                  {canEdit && (
                    <button
                      onClick={(e) => handleDeleteClick(product, e)}
                      className="absolute top-3 right-14 bg-white/90 backdrop-blur-sm hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110"
                      data-testid={`delete-product-${index}`}
                      title="Delete product"
                    >
                      <Trash size={18} weight="bold" className="text-red-600" />
                    </button>
                  )}

                  {/* "Newly added" badge — shows for the first 7 days after a
                      product is shared, then falls off on its own (computed live
                      from created_at, no flagging or cron). Bottom-left so it
                      clears the brand tag (top-left) and edit buttons (top-right). */}
                  {isNewlyAdded(product.created_at) && (
                    <div
                      className="absolute bottom-3 left-3 bg-[#2D302A] text-white text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full shadow-md"
                      style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                      data-testid={`product-new-${index}`}
                    >
                      Newly added
                    </div>
                  )}

                  {/* Bookmark toggle intentionally removed from the feed —
                      bookmarking lives on the product detail page only. */}
                </div>
                
                {/* Product Info */}
                <div className="space-y-2">
                  <h3
                    className="text-base md:text-xl tracking-tight line-clamp-1 cursor-pointer hover:opacity-70 transition-opacity"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    onClick={() => handleImageClick(product)}
                    data-testid={`product-name-${index}`}
                  >
                    {product.product_name}
                  </h3>
                  
                  {product.user_name && (
                    <p className="text-xs md:text-sm text-[#6B6A68]" data-testid={`product-user-${index}`}>
                      Shared by a community member
                    </p>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    {product.price && (
                      <span
                        className="text-[10px] md:text-xs uppercase tracking-[0.1em] text-white bg-[#2D302A] px-2 md:px-3 py-1 rounded-sm"
                        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                        data-testid={`product-price-${index}`}
                      >
                        {product.price}
                      </span>
                    )}
                    {product.heights && product.heights.length > 0 && (() => {
                      // Label the size by the product's own category (shoes → "UK 9",
                      // clothing → "9 ft"), not a hardcoded unit.
                      const pcat = categoryById[product.category_id];
                      const label = (pcat?.size_label || 'ft').trim() || 'ft';
                      const isShoe = pcat?.kind === 'shoes';
                      const sizes = product.heights.join(', ');
                      return (
                        <span
                          className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#6B6A68] bg-[#F0EFEB] px-2 md:px-3 py-1 rounded-sm"
                          style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                          data-testid={`product-heights-${index}`}
                        >
                          {isShoe ? `${label} ${sizes}` : `${sizes} ${label}`}
                        </span>
                      );
                    })()}
                    {/* Fit feedback badge intentionally hidden on the feed —
                        it's shown only on the product detail page. */}
                    {product.views >= 30 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] md:text-xs uppercase tracking-[0.1em] text-[#6B6A68] bg-[#F0EFEB] px-2 md:px-3 py-1 rounded-sm"
                        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                        data-testid={`product-views-${index}`}
                        title={`${product.views} ${product.views === 1 ? 'view' : 'views'}`}
                      >
                        <Eye size={12} weight="regular" /> {product.views.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {/* Inline banner slotted after this product's position */}
              {bannersAt(index + 1).map(renderInlineBanner)}
              </Fragment>
            ))}
          </div>

          {/* Infinite-scroll sentinel + Lottie loader. The meditating-giraffe
              animation plays while the next page is being fetched, then slowly
              sunsets once the new fits have loaded. */}
          <div className="mt-12 md:mt-16 flex flex-col items-center" data-testid="pagination-footer">
            <div ref={setLoadMoreSentinel} aria-hidden="true" />
            <PaginationLoader active={loadingMore} />
          </div>

          {/* End-of-list call to action — reached once there are no more pages.
              Nudges members to contribute so the next person finds their fit
              faster. The noun swaps to "shoes" on shoe tabs. */}
          {!hasMore && !loadingMore && (
            <div
              className="mt-16 md:mt-24 border-t border-[#E4E1D9] pt-14 md:pt-20 text-center max-w-2xl mx-auto"
              data-testid="contribute-cta"
            >
              <h2
                className="text-4xl md:text-6xl tracking-tighter leading-[1.05] mb-5"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Help us add more {isShoes ? 'shoes' : 'fits'}
              </h2>
              <p className="text-[#6B6A68] text-base md:text-lg mb-8 max-w-xl mx-auto">
                {isShoes
                  ? 'Share the shoes that actually fit your size - you’ll save someone the time and money of guessing what works for them.'
                  : 'Share the fits that actually work for your height - you’ll save someone the time and money of guessing what will fit them.'}
              </p>
              <button
                onClick={() => handleShareClick('feed_footer_cta')}
                className="bg-[#2D302A] text-white px-8 py-4 rounded-md hover:bg-[#1A1C18] transition-colors duration-300"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                data-testid="contribute-cta-button"
              >
                <span className="text-xs uppercase tracking-[0.2em]">
                  Share {isShoes ? 'Your Shoes' : 'a Fit'}
                </span>
              </button>
            </div>
          )}
          </>
        )}
      </main>

      {/* Submission Form Modal */}
      {showForm && user && (
        canEdit ? (
          <ProductSubmissionFormFull
            open={showForm}
            onClose={() => setShowForm(false)}
            onSuccess={handleFormSuccess}
          />
        ) : (
          <ProductSubmissionForm
            open={showForm}
            onClose={() => setShowForm(false)}
            onSuccess={handleFormSuccess}
            user={user}
            onUserUpdate={setUser}
          />
        )
      )}

      {/* Offline spot composer — reachable from the feed section CTA and from
          the "Offline Spot" card in the share picker, so admins (who get the
          full product form, which has no picker) can still add one. */}
      {showSpotForm && user && (
        <OfflineSpotComposer
          open={showSpotForm}
          user={user}
          categories={categories}
          onClose={() => setShowSpotForm(false)}
          onSuccess={() => setShowSpotForm(false)}
        />
      )}

      {/* Edit Form Modal */}
      {showEditForm && editingProduct && (
        <ProductEditForm
          open={showEditForm}
          onClose={() => {
            setShowEditForm(false);
            setEditingProduct(null);
          }}
          onSuccess={handleEditFormSuccess}
          product={editingProduct}
        />
      )}

      {/* One-time notification opt-in — installed app only, never on web */}
      <NotificationPrompt user={user} />

      {/* Marketing popup banner (targeted + timed; opens Share-a-Fit) */}
      <CampaignPopup campaigns={campaigns} onShareClick={() => handleShareClick('campaign_popup')} />
    </div>
  );
};

const TabPill = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`shrink-0 px-3 md:px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] border transition-colors ${
      active
        ? 'bg-[#2D302A] text-white border-[#2D302A]'
        : 'bg-white text-[#2D302A] border-[#E4E1D9] hover:border-[#2D302A]'
    }`}
    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
  >
    {children}
  </button>
);

const ProductCardSkeleton = ({ index = 0 }) => (
  <div
    className="group"
    style={{ animation: `fadeUp 0.5s ease-out ${Math.min(index * 0.08, 0.4)}s both` }}
    data-testid={`product-skeleton-${index}`}
  >
    <div className="relative overflow-hidden bg-white border border-[#E4E1D9] mb-4 aspect-[3/4]">
      <div className="absolute inset-0 skeleton-shimmer" />
    </div>
    <div className="space-y-2">
      <div className="h-4 w-3/4 bg-[#F0EFEB] rounded skeleton-shimmer" />
      <div className="h-3 w-1/2 bg-[#F0EFEB] rounded skeleton-shimmer" />
      <div className="h-3 w-2/5 bg-[#F0EFEB] rounded skeleton-shimmer" />
    </div>
  </div>
);

// Friendly page titles for GA4, so the Pages report reads as screen names
// instead of a wall of raw paths. Anything unlisted falls back to the path.
const PAGE_TITLES = {
  '/': 'Feed',
  '/inseam-guide': 'Inseam Guide',
  '/offline-spots': 'Offline Spots',
  '/login': 'Sign In',
  '/onboarding': 'Onboarding',
  '/welcome': 'Welcome Redirect',
  '/stories': 'Stories',
  '/bookmarks': 'Bookmarks',
  '/profile': 'Profile',
  '/settings': 'Settings',
  '/about': 'About',
  '/contact': 'Contact',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms',
  '/admin': 'Admin',
  '/admin/pending': 'Admin — Pending Review',
};

function AppRouter() {
  const location = useLocation();

  // The only place page_view is fired. gtag's own send_page_view is disabled in
  // index.html because it fires once per document load — which in an SPA means
  // once per session, collapsing every route onto the landing page.
  useEffect(() => {
    const path = location.pathname;
    const title =
      PAGE_TITLES[path] ||
      (path.startsWith('/product/')
        ? 'Product Detail'
        : path.startsWith('/offline-spots/')
        ? 'Offline Spot Detail'
        : path);
    trackPageView(path + location.search, title);
  }, [location.pathname, location.search]);

  // Check URL for session_id in both hash and query params (mobile compatibility)
  const hasSessionId = location.hash?.includes('session_id=') || location.search?.includes('session_id=');

  if (hasSessionId) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Persistent nav on every page except the sign-in / onboarding flow */}
      <Route path="/" element={<Layout><Discovery /></Layout>} />
      {/* No Layout wrapper — the detail page is a focused view with its own
          header and no app nav rail / bottom bar. */}
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/inseam-guide" element={<InseamGuide />} />
      <Route path="/offline-spots" element={<Layout><OfflineSpots /></Layout>} />
      <Route path="/offline-spots/:id" element={<OfflineSpotDetail />} />
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/welcome" element={<WelcomeRedirect />} />
      <Route path="/stories" element={<Layout><Stories /></Layout>} />
      <Route path="/bookmarks" element={<Layout><Bookmarks /></Layout>} />
      <Route path="/profile" element={<Layout><Profile /></Layout>} />
      <Route path="/settings" element={<Layout><Settings /></Layout>} />
      {/* Public, signed-out-reachable pages — AdSense review needs these. */}
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
      <Route path="/terms" element={<Layout><Terms /></Layout>} />
      <Route path="/admin" element={<Layout><AdminPanel /></Layout>} />
      <Route path="/admin/pending" element={<Layout><PendingReview /></Layout>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;